import { assign, first, isEqual, isNaN } from 'lodash';
import { BaseForm } from '@/modules/core/app/models/abstracts/BaseForm';
import ApiColumn from '@/modules/core/app/models/api/ApiColumn';
import { ClientSelectOption } from '@/modules/ta/client/models/ClientSelectOption';
import { ClusterSelectOption } from '@/modules/ta/cluster/models/ClusterSelectOption';
import { ClientGroupSelectOption } from '@/modules/ta/clientgroup/models/ClientGroupSelectOption';
import { FormSelectOption } from '@/modules/core/form/models/FormSelectOption';
import { DashboardFilterItem } from '@/modules/core/dashboard/models/DashboardFilterItem';
import { DataSourceType, ID_STRING } from '@/modules/core/app/constants/datasource.constants';
import { SmartCampaignSelectOption } from '@/modules/ta/smartcampaign/models/SmartCampaignSelectOption';
import { getters } from '@/modules/core/app/helpers/store';
import { AppModule } from '@/modules/core/app/constants/app.constants';
import { deepClone } from '@/modules/core/helper';
import {
  WidgetFilterConditionType,
  WidgetFilterAssociation,
} from '@/modules/ta/widget/widget.constants';
import ClientFormService from '@/modules/ta/client/services/ClientFormService';
import ClientCategoryService from '@/modules/ta/client/services/ClientCategoryService';
import { FORM_SELECT_PRIMARY_VALUE_KEYS } from '@/modules/core/form/form.constants';
import { GLOBAL_FILTER_KEY_PREFIX } from '@/modules/ta/globalfilters/globalfilters.constants';

function filterEmptyKeyValueItems(values) {
  return values.filter((item) => !(item.key === '' && item.value === ''));
}

export class DashboardFilterForm extends BaseForm {
  initialData;

  visibleFields;

  constructor({
    data,
    visibleFields = [
      DataSourceType.CLIENT,
      DataSourceType.CLIENT_GROUP,
      DataSourceType.CLIENT_CATEGORY,
      DataSourceType.CLUSTER,
      DataSourceType.SMART_CAMPAIGN,
      DataSourceType.CLIENT_ADDITIONAL_DETAILS_1,
      DataSourceType.CLIENT_ADDITIONAL_DETAILS_2,
      DataSourceType.CLIENT_ADDITIONAL_DETAILS_3,
      DataSourceType.CLIENT_STATE,
      DataSourceType.CLIENT_CITY,
      DataSourceType.CLIENT_COUNTRY,
      DataSourceType.CLIENT_NAICS_CODE,
    ],
    clientGroupIds = [],
  }) {
    const userSettings = getters.session.getUserSettings();
    const clientLabel = userSettings.isModuleAvailable(AppModule.BRAND_PACKAGE)
      ? i18n.$t('Campaign')
      : i18n.$t('Client');
    const clientGroupLabel = userSettings.isModuleAvailable(AppModule.BRAND_PACKAGE)
      ? i18n.$t('Campaign Group')
      : i18n.$t('Client Group');

    const columns = [];
    if (visibleFields.includes(DataSourceType.CLIENT)) {
      columns.push(
        new ApiColumn({
          field: DataSourceType.CLIENT,
          label: clientLabel,
        })
      );
    }

    if (visibleFields.includes(DataSourceType.CLIENT_GROUP)) {
      columns.push(
        new ApiColumn({
          field: DataSourceType.FORM_CLIENT_GROUP,
          label: clientGroupLabel,
        })
      );
    }

    if (visibleFields.includes(DataSourceType.CLIENT_CATEGORY)) {
      columns.push(
        new ApiColumn({
          field: DataSourceType.FORM_CLIENT_CATEGORY,
          label: i18n.$t('Client Category'),
        })
      );
    }

    if (visibleFields.includes(DataSourceType.CLIENT_ADDITIONAL_DETAILS_1)) {
      columns.push(
        new ApiColumn({
          field: DataSourceType.FORM_CLIENT_ADDITIONAL_DETAILS_1,
          label: i18n.$t('Client Additional Details 1'),
        })
      );
    }

    if (visibleFields.includes(DataSourceType.CLIENT_ADDITIONAL_DETAILS_2)) {
      columns.push(
        new ApiColumn({
          field: DataSourceType.FORM_CLIENT_ADDITIONAL_DETAILS_2,
          label: i18n.$t('Client Additional Details 2'),
        })
      );
    }

    if (visibleFields.includes(DataSourceType.CLIENT_ADDITIONAL_DETAILS_3)) {
      columns.push(
        new ApiColumn({
          field: DataSourceType.FORM_CLIENT_ADDITIONAL_DETAILS_3,
          label: i18n.$t('Client Additional Details 3'),
        })
      );
    }

    if (visibleFields.includes(DataSourceType.CLIENT_STATE)) {
      columns.push(
        new ApiColumn({
          field: DataSourceType.FORM_CLIENT_STATE,
          label: i18n.$t('Client State'),
        })
      );
    }

    if (visibleFields.includes(DataSourceType.CLIENT_CITY)) {
      columns.push(
        new ApiColumn({
          field: DataSourceType.FORM_CLIENT_CITY,
          label: i18n.$t('Client City'),
        })
      );
    }

    if (visibleFields.includes(DataSourceType.CLIENT_COUNTRY)) {
      columns.push(
        new ApiColumn({
          field: DataSourceType.FORM_CLIENT_COUNTRY,
          label: i18n.$t('Client Country'),
        })
      );
    }

    if (visibleFields.includes(DataSourceType.CLIENT_NAICS_CODE)) {
      columns.push(
        new ApiColumn({
          field: DataSourceType.FORM_CLIENT_NAICS_CODE,
          label: i18n.$t('NAICS Code'),
        })
      );
    }

    if (visibleFields.includes(DataSourceType.CLUSTER)) {
      columns.push(
        new ApiColumn({
          field: DataSourceType.CLUSTER,
          label: i18n.$t('Business Unit'),
        })
      );
    }

    if (visibleFields.includes(DataSourceType.SMART_CAMPAIGN)) {
      columns.push(
        new ApiColumn({
          field: DataSourceType.FORM_SMART_CAMPAIGN,
          label: i18n.$t('SmartCampaign'),
        })
      );
    }

    /**
     * @param key
     * @returns {*}
     */
    function mapFilterValue(key) {
      const filter = first(data[key].values);
      return filter
        ? {
            key: filter.id,
            value: filter.text,
          }
        : null;
    }

    // Parse global filters from filter_NN keys (new format) or fall back to legacy global_filters
    const globalFiltersFromKeys = Object.entries(data)
      .filter(
        ([key]) =>
          key.startsWith(GLOBAL_FILTER_KEY_PREFIX) &&
          !isNaN(key.slice(GLOBAL_FILTER_KEY_PREFIX.length))
      )
      .map(([key, filter]) => ({
        global_filter_id: key.slice(GLOBAL_FILTER_KEY_PREFIX.length),
        condition: filter.type || WidgetFilterConditionType.IN,
        values: (filter.values || []).map((v) => ({ id: v, text: v })),
      }));
    let globalFilters;
    if (globalFiltersFromKeys.length > 0) {
      globalFilters = globalFiltersFromKeys;
    } else if (Array.isArray(data.global_filters)) {
      globalFilters = data.global_filters;
    } else {
      globalFilters = [];
    }

    const newData = {
      client: data[DataSourceType.CLIENT] ? mapFilterValue(DataSourceType.CLIENT) : null,
      clientGroup: data[DataSourceType.CLIENT_GROUP]
        ? mapFilterValue(DataSourceType.CLIENT_GROUP)
        : null,
      clientCategory: data[DataSourceType.CLIENT_CATEGORY]
        ? mapFilterValue(DataSourceType.CLIENT_CATEGORY)
        : null,
      cluster: data[DataSourceType.CLUSTER] ? mapFilterValue(DataSourceType.CLUSTER) : null,
      smartCampaign: data[DataSourceType.SMART_CAMPAIGN]
        ? mapFilterValue(DataSourceType.SMART_CAMPAIGN)
        : null,
      clientAdditionalDetails1: data[DataSourceType.CLIENT_ADDITIONAL_DETAILS_1]
        ? mapFilterValue(DataSourceType.CLIENT_ADDITIONAL_DETAILS_1)
        : null,
      clientAdditionalDetails2: data[DataSourceType.CLIENT_ADDITIONAL_DETAILS_2]
        ? mapFilterValue(DataSourceType.CLIENT_ADDITIONAL_DETAILS_2)
        : null,
      clientAdditionalDetails3: data[DataSourceType.CLIENT_ADDITIONAL_DETAILS_3]
        ? mapFilterValue(DataSourceType.CLIENT_ADDITIONAL_DETAILS_3)
        : null,
      clientCountry: data[DataSourceType.CLIENT_COUNTRY]
        ? mapFilterValue(DataSourceType.CLIENT_COUNTRY)
        : null,
      clientState: data[DataSourceType.CLIENT_STATE]
        ? mapFilterValue(DataSourceType.CLIENT_STATE)
        : null,
      clientCity: data[DataSourceType.CLIENT_CITY]
        ? mapFilterValue(DataSourceType.CLIENT_CITY)
        : null,
      clientNaicsCode: data[DataSourceType.CLIENT_NAICS_CODE]
        ? mapFilterValue(DataSourceType.CLIENT_NAICS_CODE)
        : null,
      globalFilters,
    };

    super({ data: newData, columns });

    this.initialData = deepClone(newData);

    this.clientOptions = new ClientSelectOption({ clientGroupIds });
    this.clientGroupOptions = new ClientGroupSelectOption();
    this.clientCategoryOptions = new FormSelectOption({
      getData: async (params) => {
        const categories = await ClientCategoryService.getData({
          sort: FORM_SELECT_PRIMARY_VALUE_KEYS.NAME,
          ...params,
        });
        return {
          values: filterEmptyKeyValueItems(
            categories.map((category) => ({ key: category.id, value: category.name }))
          ),
        };
      },
    });
    this.clientAdditionalDetails1Options = new FormSelectOption({
      getData: async (params) => {
        const result = await ClientFormService.getValues(
          assign(params, {
            fields: this.removeClientFromFieldText(DataSourceType.CLIENT_ADDITIONAL_DETAILS_1),
          })
        );
        return { ...result, values: filterEmptyKeyValueItems(result?.values ?? []) };
      },
    });
    this.clientAdditionalDetails2Options = new FormSelectOption({
      getData: async (params) => {
        const result = await ClientFormService.getValues(
          assign(params, {
            fields: this.removeClientFromFieldText(DataSourceType.CLIENT_ADDITIONAL_DETAILS_2),
          })
        );
        return { ...result, values: filterEmptyKeyValueItems(result?.values ?? []) };
      },
    });
    this.clientAdditionalDetails3Options = new FormSelectOption({
      getData: async (params) => {
        const result = await ClientFormService.getValues(
          assign(params, {
            fields: this.removeClientFromFieldText(DataSourceType.CLIENT_ADDITIONAL_DETAILS_3),
          })
        );
        return { ...result, values: filterEmptyKeyValueItems(result?.values ?? []) };
      },
    });
    this.clientCountryOptions = new FormSelectOption({
      getData: async (params) => {
        const result = await ClientFormService.getValues(
          assign(params, { fields: this.removeClientFromFieldText(DataSourceType.CLIENT_COUNTRY) })
        );
        return { ...result, values: filterEmptyKeyValueItems(result?.values ?? []) };
      },
    });
    this.clientStateOptions = new FormSelectOption({
      getData: async (params) => {
        const result = await ClientFormService.getValues(
          assign(params, { fields: this.removeClientFromFieldText(DataSourceType.CLIENT_STATE) })
        );
        return { ...result, values: filterEmptyKeyValueItems(result?.values ?? []) };
      },
    });
    this.clientCityOptions = new FormSelectOption({
      getData: async (params) => {
        const result = await ClientFormService.getValues(
          assign(params, { fields: this.removeClientFromFieldText(DataSourceType.CLIENT_CITY) })
        );
        return { ...result, values: filterEmptyKeyValueItems(result?.values ?? []) };
      },
    });
    this.clientNaicsCodeOptions = new FormSelectOption({
      getData: async (params) => {
        const result = await ClientFormService.getValues(
          assign(params, {
            fields: this.removeClientFromFieldText(DataSourceType.CLIENT_NAICS_CODE),
          })
        );
        return { ...result, values: filterEmptyKeyValueItems(result?.values ?? []) };
      },
    });
    this.clusterOptions = new ClusterSelectOption();
    this.smartCampaignOption = new SmartCampaignSelectOption();
    this.visibleFields = visibleFields;
  }

  removeClientFromFieldText(field) {
    return field.replace(/^client_/, '');
  }

  hasActiveFilters() {
    const hasClientFilters = Object.keys(this.getSelectedFilters()).length > 0;
    const hasGlobalFilters =
      Array.isArray(this.data.globalFilters) &&
      this.data.globalFilters.some((f) => f.values?.length > 0);
    return hasClientFilters || hasGlobalFilters;
  }

  getSelectedFilters() {
    const selectedFilters = {
      [DataSourceType.CLIENT]: this.data.client,
      [DataSourceType.CLIENT_CATEGORY]: this.data.clientCategory,
      [DataSourceType.CLIENT_GROUP]: this.data.clientGroup,
      [DataSourceType.CLIENT_ADDITIONAL_DETAILS_1]: this.data.clientAdditionalDetails1,
      [DataSourceType.CLIENT_ADDITIONAL_DETAILS_2]: this.data.clientAdditionalDetails2,
      [DataSourceType.CLIENT_ADDITIONAL_DETAILS_3]: this.data.clientAdditionalDetails3,
      [DataSourceType.CLIENT_COUNTRY]: this.data.clientCountry,
      [DataSourceType.CLIENT_STATE]: this.data.clientState,
      [DataSourceType.CLIENT_CITY]: this.data.clientCity,
      [DataSourceType.CLIENT_NAICS_CODE]: this.data.clientNaicsCode,
      [DataSourceType.CLUSTER]: this.data.cluster,
      [DataSourceType.SMART_CAMPAIGN]: this.data.smartCampaign,
    };

    return Object.fromEntries(
      Object.entries(selectedFilters).filter(([, value]) => value !== null)
    );
  }

  hasChanged() {
    return !isEqual(this.initialData, this.data);
  }

  getOptionConfig(dataSourceType) {
    switch (dataSourceType) {
      case DataSourceType.CLIENT:
        return this.clientOptions;
      case DataSourceType.CLIENT_GROUP:
        return this.clientGroupOptions;
      case DataSourceType.CLIENT_CATEGORY:
        return this.clientCategoryOptions;
      case DataSourceType.CLIENT_ADDITIONAL_DETAILS_1:
        return this.clientAdditionalDetails1Options;
      case DataSourceType.CLIENT_ADDITIONAL_DETAILS_2:
        return this.clientAdditionalDetails2Options;
      case DataSourceType.CLIENT_ADDITIONAL_DETAILS_3:
        return this.clientAdditionalDetails3Options;
      case DataSourceType.CLIENT_COUNTRY:
        return this.clientCountryOptions;
      case DataSourceType.CLIENT_STATE:
        return this.clientStateOptions;
      case DataSourceType.CLIENT_CITY:
        return this.clientCityOptions;
      case DataSourceType.CLIENT_NAICS_CODE:
        return this.clientNaicsCodeOptions;
      case DataSourceType.CLUSTER:
        return this.clusterOptions;
      case DataSourceType.SMART_CAMPAIGN:
        return this.smartCampaignOption;
      default:
        return null;
    }
  }

  /**
   * @returns {object}
   */
  getModel() {
    if (!this.hasActiveFilters()) {
      return {};
    }

    const model = {};
    Object.entries(this.getSelectedFilters()).forEach(([filterKey, selectedEntity]) => {
      const optionConfig = this.getOptionConfig(filterKey);
      const primaryKey = optionConfig?.primaryKey ?? FORM_SELECT_PRIMARY_VALUE_KEYS.KEY;
      const valueKey = optionConfig?.valueKey ?? FORM_SELECT_PRIMARY_VALUE_KEYS.VALUE;
      const id = selectedEntity?.[primaryKey] ?? selectedEntity?.id ?? selectedEntity?.key ?? null;
      const text =
        selectedEntity?.[valueKey] ??
        selectedEntity?.name ??
        selectedEntity?.company_name ??
        selectedEntity?.value ??
        null;
      model[filterKey] = new DashboardFilterItem({
        values: [
          {
            id,
            text,
          },
        ],
      });
    });
    if (Array.isArray(this.data.globalFilters) && this.data.globalFilters.length > 0) {
      this.data.globalFilters.forEach(({ global_filter_id, condition, values }) => {
        model[`${GLOBAL_FILTER_KEY_PREFIX}${global_filter_id}`] = {
          field: ID_STRING,
          format: ID_STRING,
          label: null,
          association: WidgetFilterAssociation.AND,
          type: condition || WidgetFilterConditionType.IN,
          values: (values || []).map((v) => v.id),
        };
      });
    }
    return model;
  }

  getResetModel() {
    return {
      widget_data_warehouse: [],
      widget_sourced: {
        [getters.dashboard.getDashboard()?.id]: [],
        values: [],
      },
      [DataSourceType.CLIENT]: { field: ID_STRING, values: [], type: WidgetFilterConditionType.IN },
      [DataSourceType.CLIENT_GROUP]: {
        field: ID_STRING,
        values: [],
        type: WidgetFilterConditionType.IN,
      },
      [DataSourceType.CLIENT_CATEGORY]: {
        field: DataSourceType.CLIENT_CATEGORY,
        values: [],
        type: WidgetFilterConditionType.IN,
      },
      [DataSourceType.CLIENT_ADDITIONAL_DETAILS_1]: {
        field: DataSourceType.CLIENT_ADDITIONAL_DETAILS_1,
        values: [],
        type: WidgetFilterConditionType.IN,
      },
      [DataSourceType.CLIENT_ADDITIONAL_DETAILS_2]: {
        field: DataSourceType.CLIENT_ADDITIONAL_DETAILS_2,
        values: [],
        type: WidgetFilterConditionType.IN,
      },
      [DataSourceType.CLIENT_ADDITIONAL_DETAILS_3]: {
        field: DataSourceType.CLIENT_ADDITIONAL_DETAILS_3,
        values: [],
        type: WidgetFilterConditionType.IN,
      },
      [DataSourceType.CLIENT_CITY]: {
        field: DataSourceType.CLIENT_CITY,
        values: [],
        type: WidgetFilterConditionType.IN,
      },
      [DataSourceType.CLIENT_STATE]: {
        field: DataSourceType.CLIENT_STATE,
        values: [],
        type: WidgetFilterConditionType.IN,
      },
      [DataSourceType.CLIENT_COUNTRY]: {
        field: DataSourceType.CLIENT_COUNTRY,
        values: [],
        type: WidgetFilterConditionType.IN,
      },
      [DataSourceType.CLIENT_NAICS_CODE]: {
        field: DataSourceType.CLIENT_NAICS_CODE,
        values: [],
        type: WidgetFilterConditionType.IN,
      },
      [DataSourceType.CLUSTER]: {
        field: ID_STRING,
        values: [],
        type: WidgetFilterConditionType.IN,
      },
      [DataSourceType.SMART_CAMPAIGN]: { field: ID_STRING, values: [] },
    };
  }

  resetSelectedFilters() {
    this.data.client = null;
    this.data.clientCategory = null;
    this.data.clientGroup = null;
    this.data.clientAdditionalDetails1 = null;
    this.data.clientAdditionalDetails2 = null;
    this.data.clientAdditionalDetails3 = null;
    this.data.clientCountry = null;
    this.data.clientState = null;
    this.data.clientCity = null;
    this.data.clientNaicsCode = null;
    this.data.cluster = null;
    this.data.smartCampaign = null;
    this.data.globalFilters = [];
  }
}
